import React, { useState } from "react";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { VehicleDto } from "./VehicleView";

interface VehicleDetailsProps {
  show: boolean;
  onHide: () => void;
  vehicle: VehicleDto;
}

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "N/A";
  try {
    return new Date(dateStr).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Invalid Date";
  }
};

const formatNumber = (value: number | null | undefined): string => {
  if (value == null) return "N/A";
  return value.toLocaleString();
};

const VehicleDetails: React.FC<VehicleDetailsProps> = ({ show, onHide, vehicle }) => {
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" dialogClassName="modal-xl-custom">
        <Modal.Header closeButton>
          <Modal.Title>Vehicle Details - {vehicle.numberPlate}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-4">
              <h5 className="mb-3">General Information</h5>
              <p><strong>Vehicle ID:</strong> {vehicle.vehicleId}</p>
              <p><strong>Number Plate:</strong> {vehicle.numberPlate}</p>
              <p><strong>Vehicle Type:</strong> {vehicle.vehicleType || "N/A"}</p>
              <p><strong>Brand:</strong> {vehicle.vehicleBrand || "N/A"}</p>
              <p><strong>Model:</strong> {vehicle.vehicleModel || "N/A"}</p>
              <p><strong>Class:</strong> {vehicle.vehicleClass || "N/A"}</p>
              <p><strong>Condition:</strong> {vehicle.vehicleCondition || "N/A"}</p>
              <p><strong>Owner Name:</strong> {vehicle.ownerName || "N/A"}</p>
              <p><strong>Owner Address:</strong> {vehicle.ownerAddress || "N/A"}</p>
              <p><strong>Manufacturing Year:</strong> {formatNumber(vehicle.manufacturingYear)}</p>
              <p><strong>No. of Passengers:</strong> {formatNumber(vehicle.noOfPassengers)}</p>
              <p><strong>Parking Slot Name:</strong>{vehicle.assignedParkingSlotName||"Not Assigned"}</p>
              <h5 className="mb-3">Financial Details</h5>
              <p><strong>Value:</strong> Rs. {formatNumber(vehicle.value)}.00</p>
              <p><strong>Rent per KM:</strong> Rs. {formatNumber(vehicle.rentPerKm)}</p>
            </div>
            <div className="col-md-4">
              <h5 className="mb-3">Technical Details</h5>
              <p><strong>Engine Capacity:</strong> {formatNumber(vehicle.engineCapacity)} cc</p>
              <p><strong>Engine No:</strong> {vehicle.engineNo || "N/A"}</p>
              <p><strong>Chassis No:</strong> {vehicle.chassisNo || "N/A"}</p>
              <p><strong>Gross Weight:</strong> {formatNumber(vehicle.grossWeight)} kg</p>
              <p><strong>Fuel Type:</strong> {vehicle.vehicleFuelType || "N/A"}</p>
              <p><strong>Gear Type:</strong> {vehicle.vehicleGearType || "N/A"}</p>
              <p><strong>Features:</strong> {vehicle.vehicleFeatures?.join(", ") || "None"}</p>
              <p><strong>Color:</strong> {vehicle.color || "N/A"}</p>
              <p><strong>Tyre Size:</strong> {vehicle.tyreSize || "N/A"}</p>
              <p><strong>Front Tyre Size:</strong> {vehicle.frontTyreSize || "N/A"}</p>
              <p><strong>Rear Tyre Size:</strong> {vehicle.rearTyreSize || "N/A"}</p>
              <h5 className="mb-3">Performance</h5>
              <p><strong>Total KMs:</strong> {formatNumber(vehicle.noOfKms)}</p>
              <p><strong>In-Station Economy:</strong> {formatNumber(vehicle.inStationEconomy)} km/l</p>
              <p><strong>Out-Station Economy:</strong> {formatNumber(vehicle.outStationEconomy)} km/l</p>
            </div>
            <div className="col-md-4">
              <h5 className="mb-3">Service History</h5>
              <p><strong>General Service Standard KMs:</strong> {formatNumber(vehicle.generalServiceStandardKms)}</p>
              <p><strong>Last General Service KMs:</strong> {formatNumber(vehicle.lastGeneralServiceKms)}</p>
              <p><strong>Last General Service Date:</strong> {formatDate(vehicle.lastGeneralServiceDate)}</p>
              <p><strong>Advanced Service Standard KMs:</strong> {formatNumber(vehicle.advancedServiceStandardKms)}</p>
              <p><strong>Last Advanced Service KMs:</strong> {formatNumber(vehicle.lastAdvancedServiceKms)}</p>
              <p><strong>Last Advanced Service Date:</strong> {formatDate(vehicle.lastAdvancedServiceDate)}</p>
              <p><strong>Tyre Change Standard KMs:</strong> {formatNumber(vehicle.standardKmsForTyreChange)}</p>
              <p><strong>Last Tyre Change KMs:</strong> {formatNumber(vehicle.lastTyreChangeKms)}</p>
              <p><strong>Last Tyre Change Date:</strong> {formatDate(vehicle.lastTyreChangeDate)}</p>
              <p><strong>Status:</strong> {vehicle.vehicleStatus || "N/A"}</p>
              <p><strong>Created By:</strong> {vehicle.createdBy || "N/A"}</p>
              <p><strong>Created Date:</strong> {formatDate(vehicle.createdDate)}</p>
              <div className="col-md-8">
                <h5 className="mb-3">Images</h5>
                <p><strong>Vehicle Images:</strong></p>
                {vehicle.images && vehicle.images.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {vehicle.images.map((image, index) => (
                      <Button
                        key={`vehicle-image-${index}`}
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleImageClick(image)}
                      >
                        View Image {index + 1}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p>None</p>
                )}
                <p className="mt-3"><strong>Registration Book Images:</strong></p>
                {vehicle.bookImages && vehicle.bookImages.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {vehicle.bookImages.map((image, index) => (
                      <Button
                        key={`book-image-${index}`}
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleImageClick(image)}
                      >
                        View Book Image {index + 1}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p>None</p>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showImageModal} onHide={handleCloseImageModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>View Image</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage ? (
            <img
              src={selectedImage}
              alt="Vehicle or Registration Book"
              style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
            />
          ) : (
            <p>No image selected.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseImageModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VehicleDetails;